// src/pages/finance-portal/inventory/InventoryAdjustments.js
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper,
    Select, Skeleton, Stack, Table, TableBody, TableCell, TableHead,
    TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatDate, formatQty,
    ADJUSTMENT_STATUS_COLORS, statusChip,
} from './components/inventoryFormatters';

const REASONS = ['SHRINKAGE', 'DAMAGE', 'EXPIRED', 'RECALL', 'RECOUNT', 'FOUND',
    'DONATION', 'SAMPLE', 'INTERNAL_USE', 'WRITE_OFF', 'OPENING_BALANCE', 'OTHER'];

export default function InventoryAdjustments() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const [createOpen, setCreateOpen] = useState(false);
    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [form, setForm] = useState({
        adjustment_date: new Date().toISOString().slice(0, 10),
        location_id: null, reason: 'SHRINKAGE',
        reason_detail: '', reference: '',
        lines: [{ product: null, quantity_delta: '', unit_cost: 0, memo: '' }],
    });
    const [saving, setSaving] = useState(false);

    const fetchAdj = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/inventory/adjustments', { params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [page, perPage, statusFilter]);

    useEffect(() => { fetchAdj(); }, [fetchAdj]);

    useEffect(() => {
        axiosInstance.get('/api/finance/inventory/locations').then((r) => setLocations(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/inventory/products', { params: { per_page: 300 } })
            .then((r) => setProducts(r.data?.data || [])).catch(() => {});
    }, []);

    const updateLine = (i, k, v) => {
        const next = form.lines.slice();
        next[i] = { ...next[i], [k]: v };
        setForm((f) => ({ ...f, lines: next }));
    };

    const addLine = () => setForm((f) => ({
        ...f,
        lines: [...f.lines, { product: null, quantity_delta: '', unit_cost: 0, memo: '' }],
    }));

    const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

    const handleCreate = async () => {
        if (!form.location_id) {
            alert('Location required');
            return;
        }
        if (form.lines.some((l) => !l.product || !l.quantity_delta)) {
            alert('Every line needs a product and quantity delta');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                lines: form.lines.map((l) => ({
                    product_id: l.product.id,
                    quantity_delta: Number(l.quantity_delta),
                    unit_cost: Number(l.unit_cost) || 0,
                    memo: l.memo,
                })),
            };
            const res = await axiosInstance.post('/api/finance/inventory/adjustments', payload);
            if (res.data?.success) {
                setCreateOpen(false);
                setForm({
                    adjustment_date: new Date().toISOString().slice(0, 10),
                    location_id: null, reason: 'SHRINKAGE',
                    reason_detail: '', reference: '',
                    lines: [{ product: null, quantity_delta: '', unit_cost: 0, memo: '' }],
                });
                fetchAdj();
            }
        } catch (e) {
            alert(e.response?.data?.message || 'Create failed');
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (id) => {
        if (!window.confirm('Approve adjustment and post to GL?')) return;
        try {
            await axiosInstance.post(`/api/finance/inventory/adjustments/${id}/approve`);
            fetchAdj();
        } catch (e) {
            alert(e.response?.data?.message || 'Approval failed');
        }
    };

    const handleVoid = async (id) => {
        if (!window.confirm('Void this adjustment?')) return;
        try {
            await axiosInstance.post(`/api/finance/inventory/adjustments/${id}/void`);
            fetchAdj();
        } catch (e) {
            alert(e.response?.data?.message || 'Void failed');
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Stock Adjustments
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Shrinkage, damage, write-offs, and other manual stock changes
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    New Adjustment
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status"
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        {['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'VOID'].map((s) => (
                            <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Adj #</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Reason</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value Impact</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? Array.from({ length: 6 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={7}><Skeleton height={32} /></TableCell></TableRow>
                        )) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                No adjustments yet
                            </TableCell></TableRow>
                        ) : rows.map((a) => (
                            <TableRow key={a.id}>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{a.adjustment_number}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(a.adjustment_date)}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={a.reason?.replace('_', ' ')}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{a.location_name}</TableCell>
                                <TableCell align="right" sx={{
                                    fontFamily: FONT_FAMILY, fontWeight: 600,
                                    color: a.total_value_impact >= 0 ? '#166534' : '#991b1b',
                                }}>
                                    {a.total_value_impact >= 0 ? '+' : ''}{formatCurrency(a.total_value_impact, a.currency)}
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={a.status?.replace('_', ' ')}
                                        style={statusChip(a.status, ADJUSTMENT_STATUS_COLORS)}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell align="right">
                                    {['DRAFT', 'PENDING_APPROVAL'].includes(a.status) && (
                                        <IconButton size="small" color="success" onClick={() => handleApprove(a.id)} title="Approve">
                                            <CheckCircleIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {!['VOID'].includes(a.status) && (
                                        <IconButton size="small" color="error" onClick={() => handleVoid(a.id)} title="Void">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div" count={total} page={page}
                    onPageChange={(e, p) => setPage(p)} rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '.MuiTablePagination-toolbar': { fontFamily: FONT_FAMILY } }}
                />
            </Paper>

            {/* Create dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>New Stock Adjustment</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth type="date" label="Date"
                                value={form.adjustment_date}
                                onChange={(e) => setForm((f) => ({ ...f, adjustment_date: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Location</InputLabel>
                                <Select value={form.location_id || ''} label="Location"
                                    onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}>
                                    {locations.map((l) => (
                                        <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Reason</InputLabel>
                                <Select value={form.reason} label="Reason"
                                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}>
                                    {REASONS.map((r) => <MenuItem key={r} value={r}>{r.replace('_', ' ')}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField size="small" fullWidth label="Reason Detail"
                                value={form.reason_detail}
                                onChange={(e) => setForm((f) => ({ ...f, reason_detail: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Reference"
                                value={form.reference}
                                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Lines</Typography>
                                <Button size="small" startIcon={<AddIcon />} onClick={addLine}
                                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', color: INV_ACCENT }}>
                                    Add Line
                                </Button>
                            </Stack>
                            {form.lines.map((l, i) => (
                                <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }}>
                                    <Autocomplete size="small" sx={{ flex: 3 }}
                                        options={products}
                                        getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                                        value={l.product}
                                        onChange={(e, v) => {
                                            updateLine(i, 'product', v);
                                            if (v) updateLine(i, 'unit_cost', v.average_cost || v.unit_cost || 0);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Product" />} />
                                    <TextField size="small" sx={{ flex: 1 }} type="number" label="Qty Delta"
                                        value={l.quantity_delta}
                                        onChange={(e) => updateLine(i, 'quantity_delta', e.target.value)}
                                        helperText="+ = gain, − = loss" />
                                    <TextField size="small" sx={{ flex: 1 }} type="number" label="Unit Cost"
                                        value={l.unit_cost}
                                        onChange={(e) => updateLine(i, 'unit_cost', e.target.value)} />
                                    <TextField size="small" sx={{ flex: 2 }} label="Memo"
                                        value={l.memo}
                                        onChange={(e) => updateLine(i, 'memo', e.target.value)} />
                                    <IconButton onClick={() => removeLine(i)}><DeleteIcon /></IconButton>
                                </Stack>
                            ))}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={handleCreate}
                        sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {saving ? 'Saving…' : 'Save Draft'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
